import { startOfDay } from 'date-fns';
import { ROUTES } from '@/constants';
import { ControlStatus } from '@/types';
import { formatDate, parseDateOnlyString } from '@/utils/format';

/** The fields of a control the "Förnya kontroll" action needs. */
export interface RenewableControl {
  name: string;
  scheduledDate?: string | null;
  status: string;
  /** The list endpoint sends the type's id here and its NAME under `controlType`. */
  controlTypeId?: string | null;
  /** The detail endpoint sends the type object; the list sends a string. */
  controlType?: { id: string } | string | null;
}

/**
 * The control type's id, from whichever shape the endpoint used. The list
 * (`GET /controls`) carries `controlType` as the display name and the id in
 * `controlTypeId`; the detail response carries the object. Reading only the
 * object hid "Förnya kontroll" and "Lägg till ny" on every card.
 */
export function renewableControlTypeId(control: RenewableControl): string | null {
  if (control.controlTypeId) return control.controlTypeId;
  if (control.controlType && typeof control.controlType === 'object') return control.controlType.id || null;
  return null;
}

// Four digits that stand alone ("Egenkontroll 2025", not "ISO 14001:2025x").
const YEAR_IN_NAME = /\b(19|20)\d{2}\b/;

/**
 * Proposed name of the next control: the previous name, with its first
 * 4-digit year bumped by one when the farmer named it by year ("Egenkontroll
 * 2025" → "Egenkontroll 2026"). A name without a year stays as it is: the
 * year of the next control is not known when it is created (preparation can
 * start months or a year ahead), and a guessed "2027" on a March 2026 renewal
 * read as a fact (Tove, 2026-10-17). The date field is where the year lives.
 *
 * @param name The previous control's name.
 */
export function proposeRenewalName(name: string): string {
  return name.replace(YEAR_IN_NAME, (year) => String(Number(year) + 1));
}

/**
 * Whether "Förnya kontroll" is offered: the control has a type, its date lies
 * before today, and it is neither cancelled nor archived.
 */
export function isRenewable(control: RenewableControl, now: Date = new Date()): boolean {
  if (!renewableControlTypeId(control) || !control.scheduledDate) return false;
  if (control.status === ControlStatus.CANCELLED || control.status === ControlStatus.ARCHIVED) {
    return false;
  }
  const date = parseDateOnlyString(control.scheduledDate);
  return date !== null && date < startOfDay(now);
}

/**
 * The create-page URL prefilled for the next control of the same type as `control`.
 *
 * @throws Error when the control has no control type.
 */
export function buildRenewalUrl(control: RenewableControl): string {
  const controlTypeId = renewableControlTypeId(control);
  if (!controlTypeId) throw new Error('Kontrollen saknar kontrolltyp och kan inte förnyas');
  const params = new URLSearchParams({
    controlTypeId,
    name: proposeRenewalName(control.name),
  });
  // No date: the next control is rarely on the same day a year on, and a
  // pre-filled wrong date is worse than an empty field (Tove, 2026-10-16).
  return `${ROUTES.CREATE_CONTROL}?${params.toString()}`;
}

/**
 * Renders a carried-over answer for the summary: BOOLEAN as "Ja"/"Nej", lists
 * joined with ", ", DATE as YYYY-MM-DD, anything else as its string form.
 *
 * @param fieldType The backend FieldType string ('TEXT', 'BOOLEAN', 'DATE', ...).
 * @param value The stored answer.
 */
export function formatProposedValue(fieldType: string, value: unknown): string {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.map((item) => formatProposedValue(fieldType, item)).join(', ');
  if (fieldType === 'BOOLEAN' || typeof value === 'boolean') return value === true || value === 'true' ? 'Ja' : 'Nej';
  if (fieldType === 'DATE' && typeof value === 'string') {
    const date = parseDateOnlyString(value);
    return date ? formatDate(date) : value;
  }
  return String(value);
}
